import { Box, Button, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { ChevronRightIcon } from 'components/Icons'
import { MIN_CARD_HEIGHT } from '../cardHeight'
import { ContactPhone } from '@mui/icons-material'

const ICONS = {
  home: HomeOutlinedIcon,
  contact: ContactPhone,
}

const IconBadge = ({ icon: Icon, color = 'primary', size = 36 }) => (
  <Box
    sx={theme => ({
      width: size,
      height: size,
      borderRadius: 1.5,
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
      color: `${color}.main`,
      bgcolor: alpha(theme.palette[color].main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
    })}
  >
    <Icon fontSize="small" />
  </Box>
)

const ageCaption = (t, age) =>
  age === 0 ? t('Oldest today') : age === 1 ? t('Oldest 1 day ago') : t('Oldest {{count}} days ago', { count: age })

const Tile = ({ item, alertAfterDays, onClick }) => {
  const { t } = useTranslation()
  const age = item.oldestAt ? dayjs().startOf('day').diff(dayjs(item.oldestAt).startOf('day'), 'day') : null
  const overdue = age !== null && age > alertAfterDays
  const tone = overdue ? 'error' : 'warning'

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={event => onClick && (event.key === 'Enter' || event.key === ' ') && onClick()}
      sx={theme => ({
        pl: 1.5,
        pr: 1,
        py: 1.25,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '4px solid',
        borderLeftColor: `${tone}.main`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background-color 0.15s, box-shadow 0.15s',
        '&:hover': onClick ? { bgcolor: alpha(theme.palette[tone].main, 0.06), boxShadow: 1 } : {},
        '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
      })}
    >
      <IconBadge icon={ICONS[item.icon] ?? InboxOutlinedIcon} color={tone} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight={600} noWrap>
            {t(item.label)}
          </Typography>
          {overdue && <Chip size="small" color="error" label={t('Overdue')} sx={{ height: 20, fontSize: 11 }} />}
        </Stack>
        {age !== null && (
          <Typography
            variant="caption"
            color={overdue ? 'error.main' : 'text.secondary'}
            noWrap
            sx={{ display: 'block' }}
          >
            {ageCaption(t, age)}
          </Typography>
        )}
      </Box>

      <Typography variant="h5" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums', color: `${tone}.main` }}>
        {item.count}
      </Typography>
      {onClick && <ChevronRightIcon fontSize="small" color="action" />}
    </Stack>
  )
}

export default function CountList({
  title,
  items = [],
  viewAllLink,
  alertAfterDays = 7,
  emptyMessage = 'All caught up',
  isReady = true,
  minHeight = MIN_CARD_HEIGHT,
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const visible = (items ?? []).filter(item => item.count > 0)
  const total = visible.reduce((sum, item) => sum + item.count, 0)

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        minHeight,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {viewAllLink && (
          <Button size="small" endIcon={<ChevronRightIcon />} onClick={() => navigate(viewAllLink)}>
            {t('View all')}
          </Button>
        )}
      </Stack>

      {!isReady ? (
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Skeleton variant="text" width={160} height={40} />
          {[0, 1].map(index => (
            <Skeleton key={index} variant="rounded" height={60} />
          ))}
        </Stack>
      ) : !visible.length ? (
        <Stack alignItems="center" justifyContent="center" spacing={0.5} sx={{ flex: 1, py: 3, textAlign: 'center' }}>
          <TaskAltIcon color="success" sx={{ fontSize: 40, mb: 0.5 }} />
          <Typography variant="subtitle2">{t(emptyMessage)}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('Nothing is waiting for review')}
          </Typography>
        </Stack>
      ) : (
        <>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 1.5, mb: 1.5 }}>
            <Typography variant="h4" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('waiting for review')}
            </Typography>
          </Stack>

          <Stack spacing={1}>
            {visible.map(item => (
              <Tile
                key={item.key ?? item.label}
                item={item}
                alertAfterDays={alertAfterDays}
                onClick={item.link ? () => navigate(item.link) : undefined}
              />
            ))}
          </Stack>
        </>
      )}
    </Paper>
  )
}
